import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * RedirectGate — checks every navigation against the admin-managed redirects
 * table fetched from /api/redirects. When a match is found, performs a
 * client-side navigation to the new path.
 *
 * Important details:
 *   - The redirect map is fetched ONCE on app boot.
 *   - We re-check the current path WHENEVER the map finishes loading OR the
 *     location changes (so an initial direct-link hit still gets redirected
 *     once the map arrives).
 *
 * Mount this component INSIDE <BrowserRouter> but OUTSIDE the route tree.
 */
export function RedirectGate({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const mapRef = useRef(null); // Map<string, {to, code}>
  const [loaded, setLoaded] = useState(false);

  // Load the redirect table once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/redirects");
        if (cancelled) return;
        const m = new Map();
        for (const r of data?.items || []) {
          if (r?.from_path && r?.to_path) {
            m.set(r.from_path.toLowerCase(), { to: r.to_path, code: r.code || 301 });
          }
        }
        mapRef.current = m;
      } catch {
        mapRef.current = new Map();
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // On every location change OR after the map is loaded, look up the path
  useEffect(() => {
    if (!loaded || !mapRef.current) return;
    const rawPath = (location.pathname || "/").toLowerCase();
    const stripped = rawPath.length > 1 ? rawPath.replace(/\/+$/, "") : rawPath;
    const hit = mapRef.current.get(stripped) || mapRef.current.get(stripped + "/");
    if (hit && hit.to && hit.to.split("?")[0] !== location.pathname) {
      // Allow the redirect target to include its own query string
      navigate(hit.to, { replace: true });
    }
  }, [loaded, location.pathname, location.search, navigate]);

  return children;
}
